import { forwardRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover.core";
import { cn } from "../../../utils/utils";
import { format } from "date-fns";
import CalendarIcon from "../../../assets/svgs/common/calander.svg";
import type { DayPickerSingleProps } from "react-day-picker";
import { Calendar } from "./calander.core";

interface DateInputProps {
  label?: string;
  placeholder?: string;
  value?: Date | null;
  onChange: (date: Date | null) => void;
  error?: string;
  required?: boolean;
  fullWidth?: boolean;
  disabledDays?: DayPickerSingleProps["disabled"];
  className?: string;
}

const DateInput = forwardRef<HTMLButtonElement, DateInputProps>(
  (
    {
      label,
      placeholder = "Select date",
      value,
      onChange,
      error,
      required = false,
      fullWidth = false,
      disabledDays,
      className,
    },
    ref
  ) => {
    return (
      <div className={cn("flex flex-col gap-2", fullWidth ? "w-full" : "")}>
        {label && (
          <label className="font-medium text-base leading-none">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              type="button"
              className={cn(
                "flex justify-between items-center hover:bg-gray-50 px-6 py-4 border-2 border-black rounded-xl w-full h-16 font-medium text-left transition-colors",
                !value && "text-gray-400",
                error && "border-red-500",
                className
              )}
            >
              {value ? (
                <span className="text-black">{format(value, "PPP")}</span>
              ) : (
                <span className="text-gray-400">{placeholder}</span>
              )}
              <CalendarIcon />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 border-2 border-black rounded-xl w-auto"
            align="start"
          >
            <Calendar
              mode="single"
              selected={value || undefined}
              onSelect={(date) => onChange(date || null)}
              disabled={disabledDays}
              className="bg-white rounded-xl"
            />
          </PopoverContent>
        </Popover>

        {error && <p className="text-red-500 text-sm">{error}</p>}
      </div>
    );
  }
);

DateInput.displayName = "DateInput";

export default DateInput;
